import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, Euro } from "lucide-react";
import { API } from "../../lib/api";
import { PRICE_QUESTION_LABELS, REACTION_LABELS, REACTIONS_PRIX } from "../../lib/constants";

const tooltipStyle = { background: "#141619", border: "1px solid #2E333D", borderRadius: 8, color: "#F9FAFB", fontSize: 12 };
const CHART_COLORS = ["#FFC700", "#10B981", "#F59E0B", "#EF4444"];

const Card = ({ title, children, className = "", testid }) => (
  <div className={`border border-[#2E333D] bg-[#141619] rounded-xl p-5 ${className}`} data-testid={testid}>
    <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-4">{title}</h3>
    {children}
  </div>
);

const fmt = (v) => (v === null || v === undefined ? "—" : `${v} €`);

export default function PriceAnalysis({ stats }) {
  if (!stats) return null;
  return (
    <section className="space-y-4" data-testid="price-analysis-section">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-[#FFC700]/40 bg-[#FFC700]/5 rounded-xl px-5 py-4">
        <div>
          <h2 className="font-display text-xl font-bold uppercase tracking-wide text-[#FFC700]">Analyse du prix</h2>
          <p className="text-xs text-gray-400 mt-1">
            {stats.total} réponse{stats.total > 1 ? "s" : ""} à l'étude de prix — statistiques calculées automatiquement à partir des réponses réelles
          </p>
        </div>
        <a href={`${API}/api/admin/export.csv?lead_type=prix`} data-testid="price-export-button"
          className="inline-flex items-center gap-2 rounded-lg bg-[#FFC700] text-[#0C0D0E] font-semibold text-xs px-4 py-2.5 hover:bg-[#FFD83D] transition-colors">
          <Download size={14} /> Exporter les résultats
        </a>
      </div>

      {stats.total === 0 ? (
        <Card title="En attente de données" testid="price-analysis-empty">
          <p className="text-sm text-gray-500">Aucune réponse à l'étude de prix pour le moment. Les statistiques apparaîtront dès les premières réponses.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(PRICE_QUESTION_LABELS).map(([key, label]) => (
              <div key={key} className="border border-[#2E333D] bg-[#141619] rounded-xl p-5" data-testid={`price-stat-${key}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 leading-tight">{label}</span>
                  <Euro size={15} className="text-[#FFC700] shrink-0" />
                </div>
                <p className="mt-3 font-display text-3xl font-extrabold text-[#F9FAFB]">{fmt(stats.per_question[key]?.mean)}</p>
                <p className="text-xs text-gray-500 mt-1">moyenne — médiane : {fmt(stats.per_question[key]?.median)}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card title="Courbe de sensibilité au prix (% des répondants)" className="lg:col-span-2" testid="price-sensitivity-chart">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={stats.curve}>
                  <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                  <XAxis dataKey="price" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={32} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} %`} labelFormatter={(v) => `${v} € TTC`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="trop_bas" stroke="#9CA3AF" name="Trop peu cher" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="bonne_affaire" stroke="#FFC700" name="Bonne affaire" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="cher" stroke="#F59E0B" name="Cher (acceptable)" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="trop_cher" stroke="#EF4444" name="Trop cher" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Fourchette de prix acceptable" testid="price-range-card">
              <div className="space-y-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Seuil bas (PMC)</p>
                  <p className="font-display text-3xl font-extrabold text-[#F9FAFB]">{fmt(stats.pmc)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Seuil haut (PME)</p>
                  <p className="font-display text-3xl font-extrabold text-[#F9FAFB]">{fmt(stats.pme)}</p>
                </div>
                <div className="border border-[#FFC700]/50 bg-[#FFC700]/10 rounded-lg px-4 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#FFC700]">Fourchette acceptable</p>
                  <p className="font-display text-2xl font-extrabold text-[#FFC700]" data-testid="price-acceptable-range">
                    {stats.pmc !== null && stats.pme !== null ? `${stats.pmc} € — ${stats.pme} €` : "Données insuffisantes"}
                  </p>
                  {stats.optimal !== null && (
                    <p className="text-xs text-gray-400 mt-1">Point d'indifférence : {fmt(stats.optimal)}</p>
                  )}
                </div>
                <p className="text-[11px] text-gray-600 leading-relaxed">
                  Calculée automatiquement (méthode Van Westendorp) à partir des {stats.total} réponses réelles.
                </p>
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {[169, 199].map((price) => (
              <Card key={price} title={`Réactions au scénario ${price} € TTC`} testid={`price-reactions-${price}`}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(stats[`reactions_${price}`] || []).map((r) => ({ ...r, label: REACTION_LABELS[r.label] || r.label }))} layout="vertical">
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} />
                    <YAxis type="category" dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 10 }} width={150} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Réponses">
                      {(stats[`reactions_${price}`] || []).map((r) => (
                        <Cell key={r.label} fill={CHART_COLORS[REACTIONS_PRIX.findIndex((x) => x.value === r.label)] || "#FFC700"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Intention d'achat positive par métier (scénario 169 €)" testid="price-intention-metier">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.intention_by_metier}>
                  <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={36} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name, item) => [`${v} % (${item.payload.positive}/${item.payload.total})`, "Intentions positives"]} />
                  <Bar dataKey="pct" fill="#FFC700" radius={[4, 4, 0, 0]} name="Intention positive" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Intention d'achat positive par région (scénario 169 €)" testid="price-intention-region">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.intention_by_region}>
                  <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={36} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name, item) => [`${v} % (${item.payload.positive}/${item.payload.total})`, "Intentions positives"]} />
                  <Bar dataKey="pct" fill="#F9FAFB" radius={[4, 4, 0, 0]} name="Intention positive" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-[#2E333D] bg-[#141619] rounded-xl p-5" data-testid="price-boxes-avg">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500">STOCKBOX par répondant</p>
              <p className="mt-3 font-display text-3xl font-extrabold text-[#F9FAFB]">{stats.boxes_avg ?? "—"}</p>
              <p className="text-xs text-gray-500 mt-1">moyenne envisagée</p>
            </div>
            <div className="border border-[#2E333D] bg-[#141619] rounded-xl p-5" data-testid="price-boxes-total">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500">STOCKBOX potentielles</p>
              <p className="mt-3 font-display text-3xl font-extrabold text-[#FFC700]">{stats.boxes_total}</p>
              <p className="text-xs text-gray-500 mt-1">total déclaré</p>
            </div>
            <Card title="Suivi du lancement" testid="price-follow-launch">
              <ResponsiveContainer width="100%" height={130}>
                <PieChart>
                  <Pie data={stats.follow_launch} dataKey="count" nameKey="label" innerRadius={28} outerRadius={50} paddingAngle={3}>
                    {(stats.follow_launch || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Par source / campagne" testid="price-by-source">
              <div className="space-y-2 text-xs text-gray-300 max-h-[130px] overflow-y-auto">
                {(stats.by_source || []).map((s) => (
                  <div key={s.label} className="flex justify-between"><span>{s.label}</span><span className="font-mono text-[#FFC700]">{s.count}</span></div>
                ))}
                {(stats.by_campaign || []).map((s) => (
                  <div key={s.label} className="flex justify-between text-gray-500"><span>campagne : {s.label}</span><span className="font-mono">{s.count}</span></div>
                ))}
                {(!stats.by_source || stats.by_source.length === 0) && (!stats.by_campaign || stats.by_campaign.length === 0) && (
                  <p className="text-gray-600">Aucune donnée de source pour le moment.</p>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </section>
  );
}
